import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Plus, Trash2, Landmark, Wallet, Edit2, RefreshCw, Download, ChevronDown, ChevronUp, Upload, FileText, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMMITMENT_LABELS = {
  mutuo: 'Rata Mutuo',
  agenzia_riscossione: 'Agenzia Riscossione',
  debiti_vari: 'Debiti Vari',
  leasing: 'Leasing',
  finanziamento: 'Finanziamento',
  altro: 'Altro',
};

const formatEur = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
const formatEur2 = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(v);

export const CommitmentsTab = ({ clientId, clientName }) => {
  const [commitments, setCommitments] = useState([]);
  const [balances, setBalances] = useState([]);
  const [showCommDialog, setShowCommDialog] = useState(false);
  const [showBalDialog, setShowBalDialog] = useState(false);
  const [editingComm, setEditingComm] = useState(null);
  const [commForm, setCommForm] = useState({ type: 'mutuo', description: '', monthly_amount: '', start_date: '', end_date: '', total_remaining: '', notes: '' });
  const [balForm, setBalForm] = useState({ date: '', amount: '', bank_name: '' });

  // SP Sync state
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [syncPreview, setSyncPreview] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [expandedLoans, setExpandedLoans] = useState({});

  // Bank balances grouping
  const [bankViewMode, setBankViewMode] = useState('grouped'); // 'grouped' or 'flat'

  // Spiragli bank-statement upload state
  const [spiragliFiles, setSpiragliFiles] = useState([]);
  const [spiragliPreview, setSpiragliPreview] = useState(null);
  const [spiragliLoading, setSpiragliLoading] = useState(false);
  const [spiragliMonthIdx, setSpiragliMonthIdx] = useState(0);
  const [spiragliSelectedDrawings, setSpiragliSelectedDrawings] = useState({}); // {idx: bool}

  const isSpiragli = (clientName || '').toLowerCase().includes('spiragli');

  useEffect(() => { fetchAll(); }, [clientId]);

  const fetchAll = async () => {
    try {
      const [c, b] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/commitments`),
        axios.get(`${API}/clients/${clientId}/bank-balances`),
      ]);
      setCommitments(c.data);
      setBalances(b.data);
    } catch (e) { console.error(e); }
  };

  const saveComm = async () => {
    const payload = {
      ...commForm,
      monthly_amount: parseFloat(commForm.monthly_amount) || 0,
      total_remaining: commForm.total_remaining ? parseFloat(commForm.total_remaining) : null,
      end_date: commForm.end_date || null,
      notes: commForm.notes || null,
    };
    try {
      if (editingComm) {
        await axios.put(`${API}/clients/${clientId}/commitments/${editingComm.id}`, payload);
        toast.success("Impegno aggiornato");
      } else {
        await axios.post(`${API}/clients/${clientId}/commitments`, payload);
        toast.success("Impegno creato");
      }
      setShowCommDialog(false);
      setEditingComm(null);
      fetchAll();
    } catch (e) { toast.error("Errore"); }
  };

  const deleteComm = async (id) => {
    if (!window.confirm("Eliminare questo impegno?")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/commitments/${id}`);
      toast.success("Eliminato");
      fetchAll();
    } catch (e) { toast.error("Errore"); }
  };

  const saveBal = async () => {
    try {
      await axios.post(`${API}/clients/${clientId}/bank-balances`, {
        date: balForm.date,
        amount: parseFloat(balForm.amount) || 0,
        bank_name: balForm.bank_name || null,
      });
      toast.success("Saldo salvato");
      setShowBalDialog(false);
      fetchAll();
    } catch (e) { toast.error("Errore"); }
  };

  const deleteBal = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/bank-balances/${id}`);
      toast.success("Eliminato");
      fetchAll();
    } catch (e) { toast.error("Errore"); }
  };

  const openEditComm = (c) => {
    setEditingComm(c);
    setCommForm({
      type: c.type, description: c.description, monthly_amount: c.monthly_amount.toString(),
      start_date: c.start_date, end_date: c.end_date || '', total_remaining: c.total_remaining?.toString() || '', notes: c.notes || '',
    });
    setShowCommDialog(true);
  };

  const openNewComm = () => {
    setEditingComm(null);
    setCommForm({ type: 'mutuo', description: '', monthly_amount: '', start_date: '', end_date: '', total_remaining: '', notes: '' });
    setShowCommDialog(true);
  };

  // SP Sync functions
  const loadSyncPreview = async () => {
    setSyncLoading(true);
    try {
      const res = await axios.get(`${API}/clients/${clientId}/sync-from-sp/preview`);
      setSyncPreview(res.data);
      setShowSyncDialog(true);
    } catch (e) {
      toast.error("Errore nel caricamento anteprima SP");
    } finally {
      setSyncLoading(false);
    }
  };

  const executeSyncFromSP = async () => {
    setSyncLoading(true);
    try {
      const res = await axios.post(`${API}/clients/${clientId}/sync-from-sp`);
      toast.success(res.data.message);
      setShowSyncDialog(false);
      setSyncPreview(null);
      fetchAll();
    } catch (e) {
      toast.error("Errore nella sincronizzazione");
    } finally {
      setSyncLoading(false);
    }
  };

  const toggleLoanExpand = (code) => {
    setExpandedLoans(prev => ({ ...prev, [code]: !prev[code] }));
  };

  // Spiragli bank statement: upload + preview
  const handleSpiragliUpload = async () => {
    if (!spiragliFiles.length) return;
    setSpiragliLoading(true);
    try {
      const fd = new FormData();
      spiragliFiles.forEach(f => fd.append('files', f, f.name));
      const res = await axios.post(
        `${API}/clients/${clientId}/spiragli-bank/preview`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setSpiragliPreview(res.data);
      setSpiragliMonthIdx(0);
      // By default, all detected drawings are selected (= included in compenso soci)
      const cons = res.data.consolidated || [];
      const initialSel = {};
      cons.forEach((m, mi) => {
        (m.owner_drawings || []).forEach((d, di) => {
          initialSel[`${mi}-${di}`] = true;
        });
      });
      setSpiragliSelectedDrawings(initialSel);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore parsing PDF");
    } finally {
      setSpiragliLoading(false);
    }
  };

  const computeSelectedDrawingsTotal = (monthIdx) => {
    if (!spiragliPreview) return 0;
    const cons = spiragliPreview.consolidated[monthIdx];
    if (!cons) return 0;
    return (cons.owner_drawings || []).reduce((s, d, di) => {
      const k = `${monthIdx}-${di}`;
      return s + (spiragliSelectedDrawings[k] ? d.amount : 0);
    }, 0);
  };

  const handleSpiragliApply = async (monthIdx) => {
    if (!spiragliPreview) return;
    const cons = spiragliPreview.consolidated[monthIdx];
    if (!cons) return;
    setSpiragliLoading(true);
    try {
      const total = computeSelectedDrawingsTotal(monthIdx);
      const payload = {
        year: cons.year,
        month: cons.month,
        bank_balances: cons.bank_balances,
        loans: cons.loans,
        owner_drawings_total: total,
      };
      const res = await axios.post(
        `${API}/clients/${clientId}/spiragli-bank/apply`,
        payload,
      );
      toast.success(res.data.message);
      // Reset the just-applied month to avoid double-saves
      setSpiragliPreview(prev => {
        const next = { ...prev };
        next.consolidated = next.consolidated.map((m, i) => i === monthIdx ? { ...m, _applied: true } : m);
        return next;
      });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSpiragliLoading(false);
    }
  };

  const monthlyTotal = commitments.reduce((s, c) => s + (c.monthly_amount || 0), 0);

  // Group balances by date for total view
  const balancesByDate = {};
  balances.forEach(b => {
    if (!balancesByDate[b.date]) balancesByDate[b.date] = [];
    balancesByDate[b.date].push(b);
  });
  const sortedDates = Object.keys(balancesByDate).sort().reverse();
  const latestDate = sortedDates[0];
  const latestTotal = latestDate
    ? balancesByDate[latestDate].reduce((s, b) => s + b.amount, 0)
    : null;

  return (
    <div className="space-y-6" data-testid="commitments-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-red-400">
          <CardContent className="pt-4 pb-3">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Rate Mensili Totali</div>
            <div className="text-2xl font-bold text-red-700 tabular-nums mt-1" data-testid="total-monthly-commitments">{formatEur(monthlyTotal)}</div>
            <div className="text-xs text-muted-foreground">{commitments.length} impegni attivi</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-400">
          <CardContent className="pt-4 pb-3">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Liquidita Totale</div>
            <div className={`text-2xl font-bold tabular-nums mt-1 ${latestTotal != null && latestTotal > 0 ? 'text-blue-700' : 'text-red-700'}`} data-testid="latest-bank-balance">
              {latestTotal != null ? formatEur(latestTotal) : 'Non inserito'}
            </div>
            {latestDate && (
              <div className="text-xs text-muted-foreground">
                {balancesByDate[latestDate].length} conti - {latestDate}
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-400">
          <CardContent className="pt-4 pb-3">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Debito Residuo Attuale</div>
            <div className="text-2xl font-bold text-amber-700 tabular-nums mt-1" data-testid="total-current-remaining">
              {formatEur(commitments.reduce((s, c) => s + (c.current_remaining ?? c.total_remaining ?? 0), 0))}
            </div>
            <div className="text-xs text-muted-foreground">
              Iniziale: {formatEur(commitments.reduce((s, c) => s + (c.total_remaining || 0), 0))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sync from SP button */}
      <Card className="bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200">
        <CardContent className="py-4 flex items-center justify-between">
          <div>
            <div className="font-medium text-sm text-indigo-900">Importa da Stato Patrimoniale</div>
            <div className="text-xs text-indigo-600 mt-0.5">
              Carica automaticamente saldi banca e finanziamenti dai dati SP caricati
            </div>
          </div>
          <Button
            onClick={loadSyncPreview}
            disabled={syncLoading}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="sync-from-sp-btn"
          >
            {syncLoading ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
            Importa da SP
          </Button>
        </CardContent>
      </Card>

      {isSpiragli && (
        <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base text-emerald-900 flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Carica Estratti Conto Spiragli
                </CardTitle>
                <CardDescription className="text-emerald-700">
                  Carica BSI / BAC / Carta di Credito (anche tutti insieme). Estraiamo: <b>saldi conti</b>, <b>rate mutui</b> e <b>prelievi titolare</b> da inserire come <b>compenso soci</b> nel CE annuale.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <Input
                type="file"
                multiple
                accept="application/pdf"
                onChange={(e) => setSpiragliFiles(Array.from(e.target.files || []))}
                data-testid="spiragli-bank-files-input"
                className="bg-white"
              />
              <Button
                onClick={handleSpiragliUpload}
                disabled={!spiragliFiles.length || spiragliLoading}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="spiragli-bank-upload-btn"
              >
                {spiragliLoading ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                Analizza
              </Button>
            </div>
            {spiragliFiles.length > 0 && !spiragliPreview && (
              <div className="text-xs text-emerald-700">{spiragliFiles.length} file selezionati</div>
            )}

            {spiragliPreview && (
              <div className="bg-white border border-emerald-200 rounded-md p-3 space-y-3">
                {spiragliPreview.consolidated?.length > 1 && (
                  <div className="flex items-center gap-1 text-xs">
                    <span className="text-emerald-700">Periodo:</span>
                    {spiragliPreview.consolidated.map((m, i) => (
                      <Button
                        key={i}
                        variant={i === spiragliMonthIdx ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setSpiragliMonthIdx(i)}
                        className="h-6 px-2 text-[11px]"
                      >
                        {String(m.month).padStart(2, '0')}/{m.year}
                      </Button>
                    ))}
                  </div>
                )}

                {(() => {
                  const cons = spiragliPreview.consolidated[spiragliMonthIdx];
                  if (!cons) return null;
                  const total = computeSelectedDrawingsTotal(spiragliMonthIdx);
                  return (
                    <div className="space-y-3">
                      <div className="text-sm font-medium text-emerald-900">
                        Mese {String(cons.month).padStart(2, '0')}/{cons.year}
                        {cons._applied && (
                          <Badge className="ml-2 bg-emerald-600 text-white">
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Salvato
                          </Badge>
                        )}
                      </div>

                      {/* Saldi banca */}
                      <div>
                        <div className="text-xs font-semibold text-emerald-800 uppercase mb-1">Saldi Banca</div>
                        {cons.bank_balances?.length ? (
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            {cons.bank_balances.map((b, i) => (
                              <div key={i} className="border rounded px-2 py-1.5 text-xs flex items-center justify-between bg-slate-50">
                                <span className="font-medium">{b.bank_name}</span>
                                <span className={`tabular-nums font-semibold ${b.amount >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                                  {formatEur2(b.amount)}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs text-muted-foreground">Nessun saldo rilevato</div>
                        )}
                      </div>

                      {/* Mutui */}
                      <div>
                        <div className="text-xs font-semibold text-emerald-800 uppercase mb-1">Rate Mutui / Finanziamenti</div>
                        {cons.loans?.length ? (
                          <div className="border rounded overflow-hidden">
                            <table className="w-full text-xs">
                              <thead className="bg-slate-100 text-slate-600">
                                <tr>
                                  <th className="text-left px-2 py-1">Banca</th>
                                  <th className="text-left px-2 py-1">Contratto</th>
                                  <th className="text-right px-2 py-1">Capitale</th>
                                  <th className="text-right px-2 py-1">Interessi</th>
                                  <th className="text-right px-2 py-1">Rata</th>
                                  <th className="text-right px-2 py-1">Debito res.</th>
                                </tr>
                              </thead>
                              <tbody>
                                {cons.loans.map((ln, i) => (
                                  <tr key={i} className="border-t">
                                    <td className="px-2 py-1">{ln.bank_name}</td>
                                    <td className="px-2 py-1 font-mono">{ln.contract_code} {ln.rata_numero ? `· #${ln.rata_numero}` : ''}</td>
                                    <td className="px-2 py-1 text-right tabular-nums">{formatEur2(ln.capitale || 0)}</td>
                                    <td className="px-2 py-1 text-right tabular-nums">{formatEur2(ln.interessi || 0)}</td>
                                    <td className="px-2 py-1 text-right tabular-nums font-semibold">{formatEur2(ln.rata || 0)}</td>
                                    <td className="px-2 py-1 text-right tabular-nums text-amber-700">{formatEur2(ln.debito_residuo || 0)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="text-xs text-muted-foreground">Nessuna rata mutuo rilevata</div>
                        )}
                      </div>

                      {/* Prelievi titolare */}
                      <div>
                        <div className="text-xs font-semibold text-emerald-800 uppercase mb-1">
                          Prelievi Titolare → Compenso Soci CE
                        </div>
                        {cons.owner_drawings?.length ? (
                          <div className="space-y-1">
                            {cons.owner_drawings.map((d, di) => {
                              const k = `${spiragliMonthIdx}-${di}`;
                              return (
                                <label key={di} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 px-2 py-1 rounded">
                                  <input
                                    type="checkbox"
                                    checked={!!spiragliSelectedDrawings[k]}
                                    onChange={(e) => setSpiragliSelectedDrawings(s => ({ ...s, [k]: e.target.checked }))}
                                    data-testid={`drawing-check-${k}`}
                                  />
                                  <span className="text-slate-600 w-20">{d.date}</span>
                                  <span className="flex-1">{d.description}</span>
                                  <span className="tabular-nums font-semibold text-rose-700">{formatEur2(d.amount)}</span>
                                </label>
                              );
                            })}
                            <div className="pt-1 mt-1 border-t flex items-center justify-between text-xs">
                              <span className="font-medium text-emerald-900">Totale selezionato (compenso soci):</span>
                              <span className="tabular-nums font-bold text-rose-800">{formatEur2(total)}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-muted-foreground">Nessun prelievo titolare rilevato (solo PRELEVAMENTI in contanti)</div>
                        )}
                      </div>

                      <div className="flex justify-end pt-2 border-t border-emerald-100">
                        <Button
                          onClick={() => handleSpiragliApply(spiragliMonthIdx)}
                          disabled={spiragliLoading || cons._applied}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="spiragli-bank-apply-btn"
                        >
                          {cons._applied ? (
                            <><CheckCircle2 className="h-4 w-4 mr-1" /> Salvato</>
                          ) : (
                            <><CheckCircle2 className="h-4 w-4 mr-1" /> Salva mese {String(cons.month).padStart(2, '0')}/{cons.year}</>
                          )}
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Commitments List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-base">Impegni Finanziari</CardTitle>
            <CardDescription>Rate mutui, debiti, leasing e altri impegni ricorrenti</CardDescription>
          </div>
          <Button size="sm" onClick={openNewComm} data-testid="add-commitment-btn">
            <Plus className="h-4 w-4 mr-1" /> Nuovo Impegno
          </Button>
        </CardHeader>
        <CardContent>
          {commitments.length === 0 ? (
            <p className="text-center text-muted-foreground py-6">Nessun impegno inserito. Clicca "Nuovo Impegno" per aggiungere rate mutui, debiti, leasing.</p>
          ) : (
            <div className="space-y-2">
              {commitments.map(c => (
                <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border bg-white hover:bg-slate-50 transition" data-testid={`commitment-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <Landmark className="h-5 w-5 text-slate-400" />
                    <div>
                      <div className="font-medium text-sm">{c.description}</div>
                      <div className="flex gap-2 items-center mt-0.5">
                        <Badge variant="outline" className="text-[10px]">{COMMITMENT_LABELS[c.type] || c.type}</Badge>
                        <span className="text-xs text-muted-foreground">
                          {c.start_date} {'\u2192'} {c.end_date || 'indefinito'}
                        </span>
                      </div>
                      {c.notes && <div className="text-[11px] text-muted-foreground mt-0.5">{c.notes}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="font-bold text-sm tabular-nums">{formatEur(c.monthly_amount)}/mese</div>
                      {c.total_remaining > 0 && (
                        <div className="text-xs text-muted-foreground" data-testid={`remaining-${c.id}`}>
                          Residuo: {formatEur(c.current_remaining ?? c.total_remaining)}
                          {c.total_months > 0 && <span className="ml-1">({c.months_paid}/{c.total_months} rate)</span>}
                        </div>
                      )}
                      {c.total_remaining > 0 && c.total_months > 0 && (
                        <div className="w-24 h-1.5 bg-slate-200 rounded-full mt-1 ml-auto">
                          <div
                            className="h-full bg-green-500 rounded-full transition-all"
                            style={{ width: `${Math.min(100, (c.months_paid / c.total_months) * 100)}%` }}
                          />
                        </div>
                      )}
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditComm(c)}><Edit2 className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => deleteComm(c.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bank Balances */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-base">Saldi Banca e Liquidita</CardTitle>
            <CardDescription>Storico saldi per singolo conto</CardDescription>
          </div>
          <div className="flex gap-2">
            <div className="flex border rounded-md overflow-hidden text-xs">
              <button
                className={`px-3 py-1.5 ${bankViewMode === 'grouped' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                onClick={() => setBankViewMode('grouped')}
              >Per Mese</button>
              <button
                className={`px-3 py-1.5 ${bankViewMode === 'flat' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                onClick={() => setBankViewMode('flat')}
              >Lista</button>
            </div>
            <Button size="sm" variant="outline" onClick={() => { setBalForm({ date: new Date().toISOString().slice(0, 7), amount: '', bank_name: '' }); setShowBalDialog(true); }} data-testid="add-balance-btn">
              <Wallet className="h-4 w-4 mr-1" /> Aggiorna Saldo
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {balances.length === 0 ? (
            <p className="text-center text-muted-foreground py-6">Nessun saldo inserito. Usa "Importa da SP" per caricare automaticamente.</p>
          ) : bankViewMode === 'grouped' ? (
            <div className="space-y-3">
              {sortedDates.map(date => {
                const entries = balancesByDate[date];
                const total = entries.reduce((s, b) => s + b.amount, 0);
                return (
                  <div key={date} className="border rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between p-3 bg-slate-50">
                      <div className="font-medium text-sm">{date}</div>
                      <div className="font-bold tabular-nums text-blue-700">{formatEur2(total)}</div>
                    </div>
                    <div className="divide-y">
                      {entries.map(b => (
                        <div key={b.id} className="flex items-center justify-between px-3 py-2" data-testid={`balance-${b.id}`}>
                          <div className="text-sm text-muted-foreground">{b.bank_name || 'Senza nome'}</div>
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold tabular-nums text-sm ${b.amount >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                              {formatEur2(b.amount)}
                            </span>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-red-400" onClick={() => deleteBal(b.id)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {balances.map(b => (
                <div key={b.id} className="flex items-center justify-between p-2.5 rounded border bg-white" data-testid={`balance-${b.id}`}>
                  <div>
                    <div className="text-xs text-muted-foreground">{b.date} {b.bank_name && `· ${b.bank_name}`}</div>
                    <div className={`font-bold tabular-nums ${b.amount >= 0 ? 'text-blue-700' : 'text-red-700'}`}>{formatEur(b.amount)}</div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => deleteBal(b.id)}><Trash2 className="h-3 w-3" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Commitment Dialog */}
      <Dialog open={showCommDialog} onOpenChange={setShowCommDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingComm ? 'Modifica Impegno' : 'Nuovo Impegno Finanziario'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo</Label>
              <Select value={commForm.type} onValueChange={v => setCommForm({ ...commForm, type: v })}>
                <SelectTrigger data-testid="commitment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(COMMITMENT_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descrizione</Label>
              <Input value={commForm.description} onChange={e => setCommForm({ ...commForm, description: e.target.value })} placeholder="es. Mutuo BNL n.123" data-testid="commitment-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Rata Mensile (EUR)</Label>
                <Input type="number" value={commForm.monthly_amount} onChange={e => setCommForm({ ...commForm, monthly_amount: e.target.value })} placeholder="1500" data-testid="commitment-amount-input" />
              </div>
              <div>
                <Label>Debito Iniziale Totale (EUR)</Label>
                <Input type="number" value={commForm.total_remaining} onChange={e => setCommForm({ ...commForm, total_remaining: e.target.value })} placeholder="50000" data-testid="commitment-remaining-input" />
                <p className="text-[11px] text-muted-foreground mt-0.5">Il residuo attuale viene calcolato automaticamente</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data Inizio (YYYY-MM)</Label>
                <Input value={commForm.start_date} onChange={e => setCommForm({ ...commForm, start_date: e.target.value })} placeholder="2024-01" data-testid="commitment-start-input" />
              </div>
              <div>
                <Label>Data Fine (YYYY-MM)</Label>
                <Input value={commForm.end_date} onChange={e => setCommForm({ ...commForm, end_date: e.target.value })} placeholder="2029-12" />
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Input value={commForm.notes} onChange={e => setCommForm({ ...commForm, notes: e.target.value })} placeholder="Note aggiuntive" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommDialog(false)}>Annulla</Button>
            <Button onClick={saveComm} data-testid="save-commitment-btn">{editingComm ? 'Aggiorna' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bank Balance Dialog */}
      <Dialog open={showBalDialog} onOpenChange={setShowBalDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Aggiorna Saldo Banca</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Mese (YYYY-MM)</Label>
              <Input value={balForm.date} onChange={e => setBalForm({ ...balForm, date: e.target.value })} placeholder="2026-03" data-testid="balance-date-input" />
            </div>
            <div>
              <Label>Saldo (EUR)</Label>
              <Input type="number" value={balForm.amount} onChange={e => setBalForm({ ...balForm, amount: e.target.value })} placeholder="25000" data-testid="balance-amount-input" />
            </div>
            <div>
              <Label>Banca / Conto</Label>
              <Input value={balForm.bank_name} onChange={e => setBalForm({ ...balForm, bank_name: e.target.value })} placeholder="BNL, Intesa, Cassa contanti..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBalDialog(false)}>Annulla</Button>
            <Button onClick={saveBal} data-testid="save-balance-btn">Salva Saldo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SP Sync Preview Dialog */}
      <Dialog open={showSyncDialog} onOpenChange={setShowSyncDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Importazione da Stato Patrimoniale</DialogTitle>
            <DialogDescription>
              Anteprima dei dati che verranno importati dai periodi SP caricati
            </DialogDescription>
          </DialogHeader>
          {syncPreview && (
            <div className="space-y-4">
              {/* Bank preview */}
              <div>
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-blue-600" />
                  Conti Banca / Liquidita
                  <Badge variant="secondary" className="text-xs">{syncPreview.bank_accounts_count} conti, {syncPreview.bank_months_count} mesi</Badge>
                </h4>
                {syncPreview.bank_entries.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="text-xs">
                          <TableHead className="py-1.5">Mese</TableHead>
                          <TableHead className="py-1.5">Conto</TableHead>
                          <TableHead className="py-1.5 text-right">Saldo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {syncPreview.bank_entries.slice(-20).map((b, i) => (
                          <TableRow key={i} className="text-xs">
                            <TableCell className="py-1">{b.date}</TableCell>
                            <TableCell className="py-1">{b.name}</TableCell>
                            <TableCell className="py-1 text-right tabular-nums font-medium">{formatEur2(b.amount)}</TableCell>
                          </TableRow>
                        ))}
                        {syncPreview.bank_entries.length > 20 && (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center text-xs text-muted-foreground py-1">
                              ... e altri {syncPreview.bank_entries.length - 20} record
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Nessun conto banca trovato nello SP</p>
                )}
              </div>

              {/* Loans preview */}
              <div>
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <Landmark className="h-4 w-4 text-amber-600" />
                  Finanziamenti / Mutui
                  <Badge variant="secondary" className="text-xs">{syncPreview.loan_summaries.length} finanziamenti</Badge>
                </h4>
                {syncPreview.loan_summaries.length > 0 ? (
                  <div className="space-y-2">
                    {syncPreview.loan_summaries.map((loan, i) => (
                      <div key={i} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-sm">{loan.name}</div>
                            <div className="text-xs text-muted-foreground">Codice: {loan.code}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-sm tabular-nums">{formatEur2(loan.avg_monthly_payment)}/mese</div>
                            <div className="text-xs text-muted-foreground">
                              Saldo iniziale: {formatEur2(loan.first_balance)} | Attuale: {formatEur2(loan.last_balance)}
                            </div>
                          </div>
                        </div>
                        {loan.timeline && loan.timeline.length > 1 && (
                          <div className="mt-2">
                            <button
                              className="text-xs text-indigo-600 hover:underline flex items-center gap-1"
                              onClick={() => toggleLoanExpand(loan.code)}
                            >
                              {expandedLoans[loan.code] ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                              {expandedLoans[loan.code] ? 'Nascondi' : 'Mostra'} storico ({loan.timeline.length} mesi)
                            </button>
                            {expandedLoans[loan.code] && (
                              <div className="mt-1 border rounded overflow-hidden max-h-32 overflow-y-auto">
                                <Table>
                                  <TableBody>
                                    {loan.timeline.map((t, j) => (
                                      <TableRow key={j} className="text-xs">
                                        <TableCell className="py-0.5">{t.date}</TableCell>
                                        <TableCell className="py-0.5 text-right tabular-nums">{formatEur2(t.amount)}</TableCell>
                                        {j > 0 && (
                                          <TableCell className="py-0.5 text-right tabular-nums text-green-600">
                                            -{formatEur2(loan.timeline[j-1].amount - t.amount)}
                                          </TableCell>
                                        )}
                                        {j === 0 && <TableCell className="py-0.5" />}
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Nessun finanziamento trovato nello SP</p>
                )}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                I dati esistenti con lo stesso nome conto verranno aggiornati. I nuovi conti verranno aggiunti.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSyncDialog(false)}>Annulla</Button>
            <Button
              onClick={executeSyncFromSP}
              disabled={syncLoading}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="confirm-sync-btn"
            >
              {syncLoading ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
              Conferma Importazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommitmentsTab;
